// ======================================================
// INSPECDOOR AUTH SYSTEM - TYPESCRIPT TYPES
// ======================================================
// Vollständige Type-Definitionen für das 3-Rollen-Auth-System
// Compatible mit bestehender InspecDoor-Struktur
// ======================================================

// 1. CORE AUTH TYPES
// ======================================================

export type UserRole = 'admin' | 'verwaltung' | 'monteur';

export interface User {
  id: string;
  username: string;
  email?: string;
  password_hash: string;
  full_name: string;
  role: UserRole;
  
  // Account Status
  is_active: boolean;
  must_change_password: boolean;
  failed_login_attempts: number;
  account_locked_until?: string;
  last_login?: string;
  last_password_change?: string;
  
  // Metadata
  created_at: string;
  created_by?: string;
  updated_at: string;
  updated_by?: string;
}

export interface UserSession {
  id: string;
  user_id: string;
  session_token: string;
  expires_at: string;
  user_agent?: string;
  ip_address?: string;
  
  // Metadata
  created_at: string;
  last_activity: string;
  is_active: boolean;
  
  // Relation
  user?: User;
}

export interface AuthState {
  user: User | null;
  session: UserSession | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  permissions: UserPermissions;
}

export interface UserPermissions {
  // Module-Zugriffe
  canAccessKunden: boolean;
  canAccessLieferorte: boolean;
  canAccessStandorte: boolean;
  canAccessTueren: boolean;
  canAccessPruefungen: boolean;
  canAccessReparaturen: boolean;
  canAccessBerichte: boolean;
  
  // CRUD-Permissions
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  
  // Admin-spezifische Permissions
  canManageUsers: boolean;
  canViewLogs: boolean;
  canAccessSettings: boolean;
  
  // Spezielle Berechtigungen
  canSignInspections: boolean;
  canApproveRepairs: boolean;
  canExportData: boolean;
}

// 2. AUTH API TYPES
// ======================================================

export interface LoginRequest {
  username: string;
  password: string;
  remember_me?: boolean;
}

export interface LoginResponse {
  success: boolean;
  message?: string;
  user?: User;
  session?: UserSession;
  must_change_password?: boolean;
  account_locked?: boolean;
  lock_expires_at?: string;
}

export interface ChangePasswordRequest {
  current_password?: string;
  new_password: string;
  confirm_password: string;
  force_change?: boolean;
}

export interface ChangePasswordResponse {
  success: boolean;
  message?: string;
}

// 3. PERMISSION UTILITIES
// ======================================================

export type InspecDoorModule = 
  | 'dashboard'
  | 'kunden'
  | 'lieferorte'
  | 'standorte'
  | 'tueren'
  | 'pruefungen'
  | 'reparaturen'
  | 'berichte'
  | 'nutzer'
  | 'sync'
  | 'einstellungen';

export type CRUDAction = 'create' | 'read' | 'update' | 'delete';

// 4. ROLE-BASED PERMISSIONS MAPPING
// ======================================================

export const ROLE_PERMISSIONS: Record<UserRole, UserPermissions> = {
  admin: {
    canAccessKunden: true,
    canAccessLieferorte: true,
    canAccessStandorte: true,
    canAccessTueren: true,
    canAccessPruefungen: true,
    canAccessReparaturen: true,
    canAccessBerichte: true,
    canCreate: true,
    canRead: true,
    canUpdate: true,
    canDelete: true,
    canManageUsers: true,
    canViewLogs: true,
    canAccessSettings: true,
    canSignInspections: true,
    canApproveRepairs: true,
    canExportData: true,
  },
  
  verwaltung: {
    canAccessKunden: true,
    canAccessLieferorte: true,
    canAccessStandorte: true,
    canAccessTueren: true,
    canAccessPruefungen: true,
    canAccessReparaturen: true,
    canAccessBerichte: true,
    canCreate: true,
    canRead: true,
    canUpdate: true,
    canDelete: true,
    canManageUsers: false,
    canViewLogs: false,
    canAccessSettings: false,
    canSignInspections: true,
    canApproveRepairs: true,
    canExportData: true,
  },
  
  monteur: {
    canAccessKunden: true,
    canAccessLieferorte: true,
    canAccessStandorte: true,
    canAccessTueren: true,
    canAccessPruefungen: true,
    canAccessReparaturen: true,
    canAccessBerichte: true,
    canCreate: false,
    canRead: true,
    canUpdate: true,
    canDelete: false,
    canManageUsers: false,
    canViewLogs: false,
    canAccessSettings: false,
    canSignInspections: true,
    canApproveRepairs: false,
    canExportData: false,
  },
};

// 5. UTILITY TYPES
// ======================================================

export type PublicUser = Omit<User, 'password_hash' | 'failed_login_attempts'>;
export type FrontendUser = Omit<User, 'password_hash' | 'created_by' | 'updated_by'>;
